/**
 * ClassificationExplainabilityTree — Chain of Thought Visualizer
 *
 * Renders a 5-step visual breakdown of how the AI classified a product:
 *   1. Input Analysis   → What the user described, intent detection, language
 *   2. Vector Retrieval → RAG sources found, similarity scores, chunk origins
 *   3. GIR Rules      → GIR rules applied, chapter notes, legal reasoning
 *   4. Classification Assessment   → HS code, confidence, mode used
 *   5. Detailed Reasoning → Full AI reasoning text, sources, caveats
 *
 * Every AI decision points to a source chunk or a specific GIR rule.
 * Uses Mantine v8 Timeline + Accordion + RingProgress.
 */

import React, { useMemo, useCallback } from "react";
import {
  Alert,
  Paper,
  Title,
  Text,
  Group,
  Stack,
  Badge,
  Accordion,
  Timeline,
  RingProgress,
  Code,
  CopyButton,
  ActionIcon,
  Tooltip,
  ThemeIcon,
  Divider,
  Box,
  List,
} from "@mantine/core";

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

/** A single source reference from the RAG system */
interface SourceReference {
  /** Chunk or document ID */
  id: string;
  /** Human-readable label (e.g. "GIR Rule 1", "Chapter 84 Notes") */
  label: string;
  /** Snippet of the source text */
  text: string;
  /** Relevance score 0–1 */
  score?: number;
  /** Origin table: chunks, hs_codes, training_examples */
  origin?: string;
}

/** GIR rule application detail */
interface GIRApplication {
  /** Rule identifier (e.g. "GIR 1", "GIR 3(b)") */
  rule: string;
  /** Why this rule was applied */
  explanation: string;
  /** Whether this rule was decisive for the classification */
  decisive: boolean;
}

/** Full classification result to visualize */
export interface ExplainabilityData {
  /** Step 1: Input Analysis */
  input: {
    originalDescription: string;
    additionalInfo?: string;
    /** REMAND: May be undefined when not tracked in audit spine */
    detectedLanguage?: string;
    /** REMAND: May be undefined when not tracked in audit spine */
    detectedIntent?: "classification" | "general_knowledge" | "conversational";
    completenessScore: number;
    /** Keywords extracted from description */
    keywords?: string[];
  };

  /** Step 2: Vector Retrieval */
  retrieval: {
    sources: SourceReference[];
    /** REMAND: undefined = not tracked (render as N/A, not -1) */
    totalSearched?: number;
    /** REMAND: undefined = not tracked */
    embeddingModel?: string;
    /** Tools invoked during retrieval */
    toolsUsed: string[];
  };

  /** Step 3: Legal Logic */
  legalLogic: {
    girRulesApplied: GIRApplication[];
    chapterNotesUsed: string[];
    legalBasis: string;
    /** e.g. "Section XVI, Chapter 84" */
    sectionPath?: string;
    /** C2-B1: Per-source legal basis items with provenance IDs */
    legalBasisItems?: Array<{
      sourceType: string;
      weight: number;
      usage: string;
      excerpt: string | null;
      citationId: string | null;
      chunkId: string | null;
      documentId: string | null;
      sourceId: string | null;
    }>;
  };

  /** Step 4: Final Decision */
  decision: {
    hsCode: string;
    description: string;
    confidenceScore: number;
    confidenceLabel: string;
    /** REMAND: undefined = not tracked in audit spine */
    modeUsed?: "function_calling" | "rag";
    /** REMAND: undefined = not tracked in audit spine (render as N/A, not -1) */
    processingTimeMs?: number;
  };

  /** Step 5: Detailed Reasoning */
  reasoning: {
    fullText: string;
    caveats?: string[];
    alternativeCodes?: Array<{ code: string; reason: string }>;
  };

  /** V2.1: Nomenclature version binding for legal defensibility */
  nomenclatureVersion?: {
    code: string;
    name: string;
    effectiveFrom?: number;
  };

  /** V2.2: Applicable trade measures (duties, restrictions) */
  measures?: {
    mfnRate: number | null;
    vatRate: number | null;
    preferentialRates: Array<{
      agreement: string;
      rate: number;
      conditions: string | null;
      geoArea: string | null;
    }>;
    restrictions: Array<{
      type: string;
      description: string;
    }>;
    prohibitions: Array<{
      type: string;
      description: string;
    }>;
    dualUse: {
      category: string | null;
      regime: string | null;
    };
    hasAnyTradeBarriers: boolean;
    // V2.2: Extended legal consequences
    conditions?: Array<{
      conditionType: string;
      operator: string;
      value: string | number | null;
      sequence: number;
    }>;
    requiredDocuments?: Array<{
      documentCode: string;
      documentName: string | null;
      mandatory: boolean;
      description: string | null;
    }>;
    geoArea?: {
      areaCode: string;
      areaType: string;
      name: string;
    } | null;
  };

  /** V2.1: Classification case ID for audit trail */
  caseId?: string;
  
  /** SOURCE PROVENANCE (REMAND FIX: end-to-end evidence chain) */
  sourceProvenance?: {
    /** The source that primarily drove this classification decision */
    decisiveSource?: {
      type: "nomenclature" | "legal";
      sourceType?: string;
      sourceId?: string;
      authorityLevel?: "PRIMARY_LEGISLATION" | "SECONDARY_LEGISLATION" | "OFFICIAL_GUIDANCE" | "CASE_LAW" | "INFORMAL_GUIDANCE";
      score: number;
      reason: string;
    };
    /** Full evidence chain with contribution percentages */
    evidenceChain?: Array<{
      sourceType: "nomenclature" | "legal";
      identifier: string;
      role: "AUTHORITATIVE" | "SUPPORTING" | "CORROBORATING";
      authorityLevel?: "PRIMARY_LEGISLATION" | "SECONDARY_LEGISLATION" | "OFFICIAL_GUIDANCE" | "CASE_LAW" | "INFORMAL_GUIDANCE";
      contribution: number;
    }>;
  };
  
  /** V2.3: CN Measures with geo area resolution */
  cnMeasures?: {
    confirmed: Array<{
      measureType: "import" | "export";
      measureTypeCode: string;
      duty: string;
      originCode: string;
      originName: string | null;
      legalBase: string;
      startDate: string | null;
      endDate: string | null;
    }>;
    conditional: Array<{
      measureType: "import" | "export";
      measureTypeCode: string;
      duty: string;
      originCode: string;
      originName: string | null;
      legalBase: string;
      certificates: Array<{ code: string; type: string | null; text: string | null }>;
      requires: "ORIGIN_DECLARATION" | "CERTIFICATE" | "LICENSE" | "UNKNOWN";
    }>;
    unresolved: Array<{
      measureType: "import" | "export";
      measureTypeCode: string;
      originCode: string;
      reason: "MISSING_GEO_CONTEXT" | "MISSING_DOCUMENT_CHAIN" | "EXPIRED_MEASURE";
    }>;
    completeness: {
      status: "COMPLETE" | "PARTIAL" | "UNAVAILABLE";
      missingData: string[];
    };
  };
  
  /** V2.4: Legal consequences summary for human-readable display */
  legalConsequencesSummary?: Array<{
    severity: "INFO" | "WARNING" | "BLOCKING";
    category: "DUTY" | "CERTIFICATE" | "RESTRICTION" | "UNRESOLVED" | "LICENSE" | "PROHIBITION" | "ORIGIN";
    message: string;
    actionRequired: string | null;
    certificateCodes: string[];
    applicableOrigins: string[];
  }>;
  
  /** Universal Reasoning Engine output for explainability */
  universalReasoning?: {
    /** Recognized product family */
    productFamily: string | null;
    /** Confidence in family detection (0-1) */
    familyConfidence: number;
    /** Domain pack used for specialized logic */
    domainPackId: string | null;
    /** How routing was determined */
    routingSource: "FAMILY" | "CHAPTER_HINT" | "KEYWORD" | "NONE";
    /** Extracted classification attributes */
    extractedAttributes: Array<{
      name: string;
      value: string | null;
      status: "PRESENT" | "ABSENT" | "AMBIGUOUS";
    }>;
    /** Count of mandatory attributes found */
    foundMandatory: number;
    /** Count of mandatory attributes missing */
    missingMandatory: number;
    /** Domain-specific clarification questions */
    targetedClarifications: string[];
    /** Decision status */
    decisionStatus: "READY" | "NEEDS_INFO" | "BLOCKED";
    /** Why this decision was made */
    decisionReason: string;
    /** Maximum allowed classification depth */
    maxAllowedDepth: string;
    /** Whether depth was enforced */
    depthEnforced: boolean;
    /** Summary in Bosnian */
    summaryBs: string;
    /** Audit trail of pipeline stages */
    auditTrail: Array<{
      stage: string;
      timestamp: string;
      action: string;
    }>;
    /** Engine version */
    engineVersion: string;
  };

  /** Runtime validator/legal-chain gate evidence. */
  validationAudit?: {
    status: string;
    gateName?: string;
    finalStatus?: string;
    reasonCodes: string[];
    blockingReasons: string[];
    warnings: string[];
    dataQualityFallback: boolean;
    legalChain?: {
      status?: string;
      reasonCode?: string;
      code?: string;
      versionId?: string | null;
      sourceTable?: string;
      blockingReasons: string[];
    };
    versionIds: string[];
    sourceIds: string[];
    finalEligibility?: {
      eligible?: boolean;
      missingProductFacts: string[];
      auditComplete?: boolean;
      blockingReasons: string[];
    };
    layers?: {
      layer1Passed: boolean;
      layer2Score: number;
      layer3Passed: boolean;
      method?: string;
    };
  };
}

// ─────────────────────────────────────────────────────────────
// Helper Components
// ─────────────────────────────────────────────────────────────

function ConfidenceRing({ score }: { score: number }): React.ReactElement {
  const pct = Math.round(score * 100);
  const color = pct >= 85 ? "green" : pct >= 70 ? "yellow" : "red";
  return (
    <RingProgress
      size={80}
      thickness={8}
      roundCaps
      sections={[{ value: pct, color }]}
      label={
        <Text ta="center" fw={700} size="sm">
          {pct}%
        </Text>
      }
    />
  );
}

function StepIcon({ icon, color }: { icon: string; color: string }): React.ReactElement {
  return (
    <ThemeIcon color={color} size="lg" radius="xl" variant="light">
      <Text size="md">{icon}</Text>
    </ThemeIcon>
  );
}

// ─────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────

interface Props {
  data: ExplainabilityData;
  /** Initially expanded accordion items (step keys). Default: all expanded. */
  defaultExpanded?: string[];
}

export function ClassificationExplainabilityTree({
  data,
  defaultExpanded,
}: Props): React.ReactElement {
  const expandedDefault = useMemo(
    () => defaultExpanded ?? ["input", "retrieval", "legal", "decision", "reasoning"],
    [defaultExpanded],
  );

  // REMAND: Helper to render "N/A" for undefined values
  const na = (val: unknown, suffix = ""): string => {
    if (val === undefined || val === null) return "N/A";
    if (typeof val === "number" && val < 0) return "N/A"; // Sentinel -1 -> N/A
    return `${val}${suffix}`;
  };

  /** Build a plain-text export of the entire chain of thought. */
  const buildExportText = useCallback((): string => {
    const lines: string[] = [
      "═══ Classification Explainability Report ═══",
      "",
      "── 1. Input Analysis ──",
      `Description: ${data.input.originalDescription}`,
      data.input.additionalInfo ? `Additional Info: ${data.input.additionalInfo}` : "",
      `Language: ${na(data.input.detectedLanguage)}`,
      `Intent: ${na(data.input.detectedIntent)}`,
      `Completeness: ${Math.round(data.input.completenessScore * 100)}%`,
      data.input.keywords?.length ? `Keywords: ${data.input.keywords.join(", ")}` : "",
      "",
      "── 2. Vector Retrieval ──",
      `Embedding Model: ${na(data.retrieval.embeddingModel)}`,
      `Total Searched: ${na(data.retrieval.totalSearched)}`,
      `Sources Found: ${data.retrieval.sources.length}`,
      `Tools Used: ${data.retrieval.toolsUsed.length ? data.retrieval.toolsUsed.join(", ") : "N/A"}`,
      ...data.retrieval.sources.map(
        (s, i) =>
          `  [${i + 1}] ${s.label} (score: ${s.score?.toFixed(3) ?? "N/A"}) — ${s.text.substring(0, 120)}...`,
      ),
      "",
      "── 3. GIR Rules ──",
      `Legal Basis: ${data.legalLogic.legalBasis}`,
      data.legalLogic.sectionPath ? `Section Path: ${data.legalLogic.sectionPath}` : "",
      `GIR Rules Applied:`,
      ...data.legalLogic.girRulesApplied.map(
        (g) => `  ${g.decisive ? "★" : "○"} ${g.rule}: ${g.explanation}`,
      ),
      `Chapter Notes: ${data.legalLogic.chapterNotesUsed.join(", ") || "None"}`,
      "",
      "── 4. Classification Assessment ──",
      `Proposed HS Code: ${data.decision.hsCode}`,
      `Description: ${data.decision.description}`,
      `Confidence: ${data.decision.confidenceLabel} (${Math.round(data.decision.confidenceScore * 100)}%)`,
      `Mode: ${na(data.decision.modeUsed)}`,
      `Processing Time: ${na(data.decision.processingTimeMs, "ms")}`,
      "",
      "── 5. Reasoning ──",
      data.reasoning.fullText,
      ...(data.reasoning.caveats?.length
        ? ["", "Caveats:", ...data.reasoning.caveats.map((c) => `  ⚠ ${c}`)]
        : []),
      ...(data.reasoning.alternativeCodes?.length
        ? [
            "",
            "Alternative Codes:",
            ...data.reasoning.alternativeCodes.map((a) => `  ${a.code}: ${a.reason}`),
          ]
        : []),
      ...(data.universalReasoning
        ? [
            "",
            "── Universal Reasoning Engine ──",
            `Product Family: ${data.universalReasoning.productFamily || "None"} (${Math.round(data.universalReasoning.familyConfidence * 100)}%)`,
            `Domain Pack: ${data.universalReasoning.domainPackId || "None"}`,
            `Routing Source: ${data.universalReasoning.routingSource}`,
            `Decision: ${data.universalReasoning.decisionStatus} — ${data.universalReasoning.decisionReason}`,
            `Attributes: ${data.universalReasoning.foundMandatory} found, ${data.universalReasoning.missingMandatory} missing`,
            `Max Depth: ${data.universalReasoning.maxAllowedDepth}${data.universalReasoning.depthEnforced ? " (enforced)" : ""}`,
            data.universalReasoning.targetedClarifications.length > 0
              ? `Clarifications: ${data.universalReasoning.targetedClarifications.join("; ")}`
              : "",
            `Summary: ${data.universalReasoning.summaryBs}`,
          ]
        : []),
    ];
    return lines.filter(Boolean).join("\n");
  }, [data]);

  return (
    <Paper shadow="sm" p="lg" radius="md" withBorder>
      <Group justify="space-between" mb="md">
        <Title order={3}>Chain of Thought</Title>
        <Group gap="xs">
          <Badge variant="light" color="blue" size="lg">
            {data.decision.hsCode}
          </Badge>
          <CopyButton value={buildExportText()}>
            {({ copied, copy }) => (
              <Tooltip label={copied ? "Copied!" : "Copy full report"}>
                <ActionIcon
                  color={copied ? "teal" : "gray"}
                  variant="subtle"
                  onClick={copy}
                  size="lg"
                >
                  <Text size="md">{copied ? "✓" : "📋"}</Text>
                </ActionIcon>
              </Tooltip>
            )}
          </CopyButton>
        </Group>
      </Group>

      <Divider mb="md" />

      {/* Timeline overview — compact visual */}
      <Timeline active={data.universalReasoning ? 5 : 4} bulletSize={28} lineWidth={2} mb="lg">
        <Timeline.Item
          bullet={<Text size="xs">1</Text>}
          title="Input Analysis"
        >
          <Text c="dimmed" size="xs">
            {data.input.detectedLanguage ?? "N/A"} · {data.input.detectedIntent ?? "N/A"} ·{" "}
            {Math.round(data.input.completenessScore * 100)}% complete
          </Text>
        </Timeline.Item>
        {data.universalReasoning && (
          <Timeline.Item
            bullet={<Text size="xs">⚙</Text>}
            title="Universal Reasoning"
          >
            <Text c="dimmed" size="xs">
              {data.universalReasoning.productFamily || "Unknown"} · {data.universalReasoning.decisionStatus} · {data.universalReasoning.foundMandatory}/{data.universalReasoning.foundMandatory + data.universalReasoning.missingMandatory} attrs
            </Text>
          </Timeline.Item>
        )}
        <Timeline.Item
          bullet={<Text size="xs">2</Text>}
          title="Vector Retrieval"
        >
          <Text c="dimmed" size="xs">
            {data.retrieval.sources.length} sources · {data.retrieval.toolsUsed.length} tools
          </Text>
        </Timeline.Item>
        <Timeline.Item
          bullet={<Text size="xs">3</Text>}
          title="GIR Rules"
        >
          <Text c="dimmed" size="xs">
            {data.legalLogic.girRulesApplied.length} GIR rules · {data.legalLogic.chapterNotesUsed.length} chapter notes
          </Text>
        </Timeline.Item>
        <Timeline.Item
          bullet={<Text size="xs">4</Text>}
          title="Classification Assessment"
        >
          <Text c="dimmed" size="xs">
            {data.decision.hsCode} · {data.decision.confidenceLabel} · {data.decision.processingTimeMs !== undefined ? `${data.decision.processingTimeMs}ms` : "N/A"}
          </Text>
        </Timeline.Item>
        <Timeline.Item
          bullet={<Text size="xs">5</Text>}
          title="Detailed Reasoning"
        >
          <Text c="dimmed" size="xs">
            {data.reasoning.alternativeCodes?.length ?? 0} alternatives ·{" "}
            {data.reasoning.caveats?.length ?? 0} caveats
          </Text>
        </Timeline.Item>
      </Timeline>

      {/* Expandable detail sections */}
      <Accordion multiple defaultValue={expandedDefault} variant="separated">
        {/* ── Step 1: Input Analysis ── */}
        <Accordion.Item value="input">
          <Accordion.Control icon={<StepIcon icon="📝" color="blue" />}>
            <Text fw={600}>1. Input Analysis</Text>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="sm">
              <Box>
                <Text size="sm" fw={600} c="dimmed">Product Description</Text>
                <Text size="sm">{data.input.originalDescription}</Text>
              </Box>
              {data.input.additionalInfo && (
                <Box>
                  <Text size="sm" fw={600} c="dimmed">Additional Information</Text>
                  <Text size="sm">{data.input.additionalInfo}</Text>
                </Box>
              )}
              <Group>
                <Badge variant="outline" color="blue">{data.input.detectedLanguage}</Badge>
                <Badge variant="outline" color="violet">{data.input.detectedIntent}</Badge>
                <Badge
                  variant="outline"
                  color={data.input.completenessScore >= 0.7 ? "green" : "orange"}
                >
                  {Math.round(data.input.completenessScore * 100)}% complete
                </Badge>
              </Group>
              {data.input.keywords && data.input.keywords.length > 0 && (
                <Box>
                  <Text size="sm" fw={600} c="dimmed">Keywords</Text>
                  <Group gap="xs">
                    {data.input.keywords.map((kw, i) => (
                      <Badge key={i} size="sm" variant="light">{kw}</Badge>
                    ))}
                  </Group>
                </Box>
              )}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        {/* Runtime validator/legal-chain gate */}
        {data.validationAudit && (
          <Accordion.Item value="validator-gate" data-testid="validator-gate-accordion">
            <Accordion.Control icon={<StepIcon icon="VG" color="red" />}>
              <Group>
                <Text fw={600}>Validator Gate</Text>
                <Badge
                  variant="light"
                  color={
                    data.validationAudit.finalStatus === "FINAL" &&
                    data.validationAudit.finalEligibility?.eligible === true
                      ? "green"
                      : data.validationAudit.finalStatus === "STOP"
                        ? "red"
                        : "yellow"
                  }
                  size="sm"
                >
                  {data.validationAudit.finalStatus || data.validationAudit.status}
                </Badge>
                {data.validationAudit.dataQualityFallback && (
                  <Badge variant="outline" color="orange" size="sm">
                    Data-quality fallback
                  </Badge>
                )}
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                <Group gap="xs">
                  <Badge variant="light" color="gray">
                    {data.validationAudit.gateName || "gate not reported"}
                  </Badge>
                  {data.validationAudit.layers && (
                    <>
                      <Badge color={data.validationAudit.layers.layer1Passed ? "green" : "red"} variant="light">
                        L1 {data.validationAudit.layers.layer1Passed ? "passed" : "blocked"}
                      </Badge>
                      <Badge color={data.validationAudit.layers.layer2Score >= 0.85 ? "green" : "orange"} variant="light">
                        L2 {Math.round(data.validationAudit.layers.layer2Score * 100)}%
                      </Badge>
                      <Badge color={data.validationAudit.layers.layer3Passed ? "green" : "red"} variant="light">
                        L3 {data.validationAudit.layers.layer3Passed ? "passed" : "blocked"}
                      </Badge>
                    </>
                  )}
                </Group>

                {data.validationAudit.reasonCodes.length > 0 && (
                  <Box>
                    <Text size="sm" fw={600} mb="xs">Reason codes</Text>
                    <List spacing="xs" size="sm">
                      {data.validationAudit.reasonCodes.map((code) => (
                        <List.Item key={code}>{code}</List.Item>
                      ))}
                    </List>
                  </Box>
                )}

                {data.validationAudit.finalEligibility && (
                  <Box>
                    <Text size="sm" fw={600} mb="xs">Final eligibility</Text>
                    <Text size="sm">
                      Eligible: {data.validationAudit.finalEligibility.eligible ? "yes" : "no"}; audit complete:{" "}
                      {data.validationAudit.finalEligibility.auditComplete ? "yes" : "no"}
                    </Text>
                    {data.validationAudit.finalEligibility.missingProductFacts.length > 0 && (
                      <List spacing="xs" size="sm" mt="xs">
                        {data.validationAudit.finalEligibility.missingProductFacts.map((fact) => (
                          <List.Item key={fact}>Missing product fact: {fact}</List.Item>
                        ))}
                      </List>
                    )}
                  </Box>
                )}

                {data.validationAudit.legalChain && (
                  <Box>
                    <Text size="sm" fw={600} mb="xs">Legal chain</Text>
                    <Group gap="xs">
                      <Badge color={data.validationAudit.legalChain.status === "OK" ? "green" : "red"} variant="light">
                        {data.validationAudit.legalChain.status || "UNKNOWN"}
                      </Badge>
                      {data.validationAudit.legalChain.reasonCode && (
                        <Badge color="gray" variant="outline">{data.validationAudit.legalChain.reasonCode}</Badge>
                      )}
                      {data.validationAudit.legalChain.sourceTable && (
                        <Badge color="blue" variant="light">{data.validationAudit.legalChain.sourceTable}</Badge>
                      )}
                    </Group>
                  </Box>
                )}

                {data.validationAudit.warnings.length > 0 && (
                  <Alert color="yellow" variant="light" title="Warnings separate from legal certainty">
                    <List spacing="xs" size="sm">
                      {data.validationAudit.warnings.map((warning) => (
                        <List.Item key={warning}>{warning}</List.Item>
                      ))}
                    </List>
                  </Alert>
                )}

                <Group gap="xs">
                  {data.validationAudit.versionIds.map((id) => (
                    <Badge key={id} variant="outline" color="violet">Version {id}</Badge>
                  ))}
                  {data.validationAudit.sourceIds.map((id) => (
                    <Badge key={id} variant="outline" color="indigo">Source {id}</Badge>
                  ))}
                </Group>

                {data.validationAudit.blockingReasons.length > 0 && (
                  <Alert color="red" variant="light" title="Blocking reasons">
                    <List spacing="xs" size="sm">
                      {data.validationAudit.blockingReasons.map((reason) => (
                        <List.Item key={reason}>{reason}</List.Item>
                      ))}
                    </List>
                  </Alert>
                )}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}

        {/* ── Universal Reasoning Engine ── */}
        {data.universalReasoning && (
          <Accordion.Item value="universalReasoning" data-testid="ure-accordion">
            <Accordion.Control icon={<StepIcon icon="⚙️" color="cyan" />} data-testid="ure-accordion-control">
              <Group gap="xs">
                <Text fw={600}>Universal Reasoning Engine</Text>
                <Badge
                  color={data.universalReasoning.decisionStatus === "READY" ? "green" : data.universalReasoning.decisionStatus === "NEEDS_INFO" ? "orange" : "blue"}
                  size="xs"
                >
                  {data.universalReasoning.decisionStatus}
                </Badge>
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                {/* Product Family & Domain */}
                <Group gap="lg">
                  <Box data-testid="ure-product-family">
                    <Text size="xs" c="dimmed">Product Family</Text>
                    <Group gap="xs">
                      <Text fw={700}>{data.universalReasoning.productFamily || "Not Identified"}</Text>
                      <Badge variant="light" color="cyan" size="sm">
                        {Math.round(data.universalReasoning.familyConfidence * 100)}%
                      </Badge>
                    </Group>
                  </Box>
                  <Box data-testid="ure-domain-pack">
                    <Text size="xs" c="dimmed">Domain Pack</Text>
                    <Text fw={600}>{data.universalReasoning.domainPackId || "None"}</Text>
                  </Box>
                  <Box>
                    <Text size="xs" c="dimmed">Routing Source</Text>
                    <Badge variant="outline" size="sm">{data.universalReasoning.routingSource}</Badge>
                  </Box>
                </Group>

                {/* Decision Status */}
                <Box data-testid="ure-decision-status">
                  <Text size="sm" fw={600} c="dimmed" mb="xs">Decision</Text>
                  <Group gap="xs">
                    <Badge
                      color={data.universalReasoning.decisionStatus === "READY" ? "green" : data.universalReasoning.decisionStatus === "NEEDS_INFO" ? "orange" : "blue"}
                      size="lg"
                    >
                      {data.universalReasoning.decisionStatus}
                    </Badge>
                    <Text size="sm">{data.universalReasoning.decisionReason}</Text>
                  </Group>
                </Box>

                {/* Extracted Attributes */}
                {data.universalReasoning.extractedAttributes.length > 0 && (
                  <Box data-testid="ure-extracted-attributes">
                    <Text size="sm" fw={600} c="dimmed" mb="xs">
                      Extracted Attributes ({data.universalReasoning.foundMandatory} found, {data.universalReasoning.missingMandatory} missing)
                    </Text>
                    <Stack gap="xs">
                      {data.universalReasoning.extractedAttributes.map((attr, i) => (
                        <Group key={i} gap="xs">
                          <Badge
                            variant={attr.status === "PRESENT" ? "filled" : "outline"}
                            color={attr.status === "PRESENT" ? "green" : attr.status === "ABSENT" ? "red" : "gray"}
                            size="sm"
                          >
                            {attr.name}
                          </Badge>
                          <Text size="sm">{attr.value || "—"}</Text>
                        </Group>
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Targeted Clarifications */}
                {data.universalReasoning.targetedClarifications.length > 0 && (
                  <Box data-testid="ure-targeted-clarifications">
                    <Text size="sm" fw={600} c="orange" mb="xs">Targeted Clarifications</Text>
                    <List spacing="xs" size="sm">
                      {data.universalReasoning.targetedClarifications.map((q, i) => (
                        <List.Item key={i}>
                          <Text size="sm">{q}</Text>
                        </List.Item>
                      ))}
                    </List>
                  </Box>
                )}

                {/* Depth & Engine Info */}
                <Group gap="lg">
                  <Box>
                    <Text size="xs" c="dimmed">Max Depth</Text>
                    <Group gap="xs">
                      <Text fw={600}>{data.universalReasoning.maxAllowedDepth}</Text>
                      {data.universalReasoning.depthEnforced && (
                        <Badge variant="light" color="yellow" size="xs">Enforced</Badge>
                      )}
                    </Group>
                  </Box>
                  <Box>
                    <Text size="xs" c="dimmed">Engine Version</Text>
                    <Code>{data.universalReasoning.engineVersion}</Code>
                  </Box>
                </Group>

                {/* Summary */}
                <Box>
                  <Text size="sm" fw={600} c="dimmed" mb="xs">Summary (BS)</Text>
                  <Paper p="sm" withBorder radius="sm" bg="gray.0">
                    <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                      {data.universalReasoning.summaryBs}
                    </Text>
                  </Paper>
                </Box>

                {/* Audit Trail */}
                {data.universalReasoning.auditTrail.length > 0 && (
                  <Box>
                    <Text size="sm" fw={600} c="dimmed" mb="xs">Audit Trail</Text>
                    <Stack gap="xs">
                      {data.universalReasoning.auditTrail.map((entry, i) => (
                        <Group key={i} gap="xs">
                          <Badge variant="dot" color="gray" size="xs">{entry.stage}</Badge>
                          <Text size="xs" c="dimmed">{entry.timestamp}</Text>
                          <Text size="xs">{entry.action}</Text>
                        </Group>
                      ))}
                    </Stack>
                  </Box>
                )}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}

        {/* ── Step 2: Vector Retrieval ── */}
        <Accordion.Item value="retrieval">
          <Accordion.Control icon={<StepIcon icon="🔍" color="grape" />}>
            <Text fw={600}>2. Vector Retrieval</Text>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="sm">
              <Group>
                <Badge variant="light" color="grape">{data.retrieval.embeddingModel}</Badge>
                <Text size="sm" c="dimmed">
                  {data.retrieval.sources.length} of {data.retrieval.totalSearched} results used
                </Text>
              </Group>
              <Box>
                <Text size="sm" fw={600} c="dimmed">Tools Invoked</Text>
                <Group gap="xs">
                  {data.retrieval.toolsUsed.map((tool, i) => (
                    <Badge key={i} size="sm" variant="dot" color="grape">{tool}</Badge>
                  ))}
                </Group>
              </Box>
              {data.retrieval.sources.length > 0 && (
                <Box>
                  <Text size="sm" fw={600} c="dimmed" mb="xs">Sources</Text>
                  {data.retrieval.sources.map((src, i) => (
                    <Paper key={src.id} p="xs" mb="xs" withBorder radius="sm" bg="gray.0">
                      <Group justify="space-between">
                        <Text size="sm" fw={600}>{src.label}</Text>
                        {src.score !== undefined && (
                          <Badge size="xs" color="grape">
                            {(src.score * 100).toFixed(1)}%
                          </Badge>
                        )}
                      </Group>
                      <Text size="xs" c="dimmed" lineClamp={2}>{src.text}</Text>
                      {src.origin && (
                        <Badge size="xs" variant="outline" mt={4}>{src.origin}</Badge>
                      )}
                    </Paper>
                  ))}
                </Box>
              )}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        {/* ── Step 3: Legal Logic ── */}
        <Accordion.Item value="legal">
          <Accordion.Control icon={<StepIcon icon="⚖️" color="orange" />}>
            <Text fw={600}>3. GIR Rules</Text>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="sm">
              <Box>
                <Text size="sm" fw={600} c="dimmed">Legal Basis</Text>
                <Text size="sm">{data.legalLogic.legalBasis}</Text>
              </Box>
              {/* C2-B1: Per-source legal basis items with provenance IDs */}
              {data.legalLogic.legalBasisItems && data.legalLogic.legalBasisItems.length > 0 && (
                <Box>
                  <Text size="sm" fw={600} c="dimmed" mb="xs">Evidence References</Text>
                  <Stack gap="xs">
                    {data.legalLogic.legalBasisItems.map((item, i) => (
                      <Paper key={i} p="xs" withBorder radius="sm">
                        <Group gap="xs" mb="xs">
                          <Badge variant="filled" color="orange" size="sm">
                            {item.sourceType}
                          </Badge>
                          <Badge variant="light" color="gray" size="sm">
                            Weight: {item.weight}
                          </Badge>
                          <Badge variant="outline" color="blue" size="sm">
                            {item.usage}
                          </Badge>
                        </Group>
                        <Text size="xs" c="dimmed" mb="xs">
                          {item.excerpt || "No excerpt available"}
                        </Text>
                        <Group gap="xs">
                          {item.citationId && (
                            <Tooltip label="Citation ID">
                              <Badge variant="outline" color="violet" size="xs">
                                <Code fz={10}>C: {item.citationId}</Code>
                              </Badge>
                            </Tooltip>
                          )}
                          {item.chunkId && (
                            <Tooltip label="Chunk ID">
                              <Badge variant="outline" color="teal" size="xs">
                                <Code fz={10}>K: {item.chunkId}</Code>
                              </Badge>
                            </Tooltip>
                          )}
                          {item.documentId && (
                            <Tooltip label="Document ID">
                              <Badge variant="outline" color="blue" size="xs">
                                <Code fz={10}>D: {item.documentId}</Code>
                              </Badge>
                            </Tooltip>
                          )}
                          {item.sourceId && (
                            <Tooltip label="Source ID">
                              <Badge variant="outline" color="indigo" size="xs">
                                <Code fz={10}>S: {item.sourceId}</Code>
                              </Badge>
                            </Tooltip>
                          )}
                          {!item.citationId && !item.chunkId && !item.documentId && !item.sourceId && (
                            <Text size="xs" c="dimmed" fs="italic">No provenance IDs available</Text>
                          )}
                        </Group>
                      </Paper>
                    ))}
                  </Stack>
                </Box>
              )}
              {data.legalLogic.sectionPath && (
                <Box>
                  <Text size="sm" fw={600} c="dimmed">Section Path</Text>
                  <Code>{data.legalLogic.sectionPath}</Code>
                </Box>
              )}
              {data.legalLogic.girRulesApplied.length > 0 && (
                <Box>
                  <Text size="sm" fw={600} c="dimmed" mb="xs">GIR Rules Applied</Text>
                  <List spacing="xs" size="sm">
                    {data.legalLogic.girRulesApplied.map((gir, i) => (
                      <List.Item key={i}>
                        <Group gap="xs" align="flex-start">
                          <Badge
                            size="sm"
                            color={gir.decisive ? "red" : "gray"}
                            variant={gir.decisive ? "filled" : "outline"}
                          >
                            {gir.rule}
                          </Badge>
                          <Text size="sm" style={{ flex: 1 }}>
                            {gir.explanation}
                            {gir.decisive && (
                              <Text component="span" c="red" fw={600}> (decisive)</Text>
                            )}
                          </Text>
                        </Group>
                      </List.Item>
                    ))}
                  </List>
                </Box>
              )}
              {data.legalLogic.chapterNotesUsed.length > 0 && (
                <Box>
                  <Text size="sm" fw={600} c="dimmed">Chapter Notes Referenced</Text>
                  <Group gap="xs">
                    {data.legalLogic.chapterNotesUsed.map((note, i) => (
                      <Badge key={i} size="sm" variant="light" color="orange">{note}</Badge>
                    ))}
                  </Group>
                </Box>
              )}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        {/* ── Step 4: Final Decision ── */}
        <Accordion.Item value="decision">
          <Accordion.Control icon={<StepIcon icon="✅" color="green" />}>
            <Text fw={600}>4. Classification Assessment</Text>
          </Accordion.Control>
          <Accordion.Panel>
            <Group align="flex-start" gap="lg">
              <ConfidenceRing score={data.decision.confidenceScore} />
              <Stack gap="xs" style={{ flex: 1 }}>
                <Group>
                  <Text size="xl" fw={700} ff="monospace">{data.decision.hsCode}</Text>
                  <Badge
                    color={data.decision.confidenceScore >= 0.85 ? "green" : data.decision.confidenceScore >= 0.7 ? "yellow" : "red"}
                    size="lg"
                  >
                    {data.decision.confidenceLabel}
                  </Badge>
                </Group>
                <Text size="sm">{data.decision.description}</Text>
                <Group gap="xs">
                  <Badge variant="dot" color="blue">
                    Mode: {data.decision.modeUsed === "function_calling" ? "Function Calling" : "RAG"}
                  </Badge>
                  <Badge variant="dot" color="gray">
                    {data.decision.processingTimeMs}ms
                  </Badge>
                </Group>
              </Stack>
            </Group>
          </Accordion.Panel>
        </Accordion.Item>

        {/* ── Step 5: Detailed Reasoning ── */}
        <Accordion.Item value="reasoning">
          <Accordion.Control icon={<StepIcon icon="🧠" color="indigo" />}>
            <Text fw={600}>5. Detailed Reasoning</Text>
          </Accordion.Control>
          <Accordion.Panel>
            <Stack gap="sm">
              <Text size="sm" style={{ whiteSpace: "pre-wrap" }}>
                {data.reasoning.fullText}
              </Text>
              {data.reasoning.caveats && data.reasoning.caveats.length > 0 && (
                <Box>
                  <Text size="sm" fw={600} c="orange" mb="xs">⚠ Caveats</Text>
                  <List spacing="xs" size="sm">
                    {data.reasoning.caveats.map((caveat, i) => (
                      <List.Item key={i}>
                        <Text size="sm" c="orange.8">{caveat}</Text>
                      </List.Item>
                    ))}
                  </List>
                </Box>
              )}
              {data.reasoning.alternativeCodes && data.reasoning.alternativeCodes.length > 0 && (
                <Box>
                  <Text size="sm" fw={600} c="dimmed" mb="xs">Alternative Codes Considered</Text>
                  {data.reasoning.alternativeCodes.map((alt, i) => (
                    <Group key={i} gap="xs" mb={4}>
                      <Badge variant="outline" size="sm" ff="monospace">{alt.code}</Badge>
                      <Text size="xs" c="dimmed">{alt.reason}</Text>
                    </Group>
                  ))}
                </Box>
              )}
            </Stack>
          </Accordion.Panel>
        </Accordion.Item>

        {/* ── Step 6: Trade Measures (Duties & Restrictions) ── */}
        {data.measures && (
          <Accordion.Item value="measures">
            <Accordion.Control icon={<StepIcon icon="💰" color="teal" />}>
              <Group gap="xs">
                <Text fw={600}>6. Trade Measures</Text>
                {data.measures.hasAnyTradeBarriers && (
                  <Badge color="orange" size="xs">Barriers Apply</Badge>
                )}
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                {/* Duty Rates */}
                <Box>
                  <Text size="sm" fw={600} mb="xs">Duty Rates</Text>
                  <Group gap="md">
                    <Box>
                      <Text size="xs" c="dimmed">MFN Rate</Text>
                      <Text fw={700}>
                        {data.measures.mfnRate !== null ? `${data.measures.mfnRate}%` : "N/A"}
                      </Text>
                    </Box>
                    <Box>
                      <Text size="xs" c="dimmed">VAT Rate</Text>
                      <Text fw={700}>
                        {data.measures.vatRate !== null ? `${data.measures.vatRate}%` : "17%"}
                      </Text>
                    </Box>
                  </Group>
                </Box>

                {/* Preferential Rates */}
                {data.measures.preferentialRates.length > 0 && (
                  <Box>
                    <Text size="sm" fw={600} mb="xs">Preferential Rates</Text>
                    <Stack gap="xs">
                      {data.measures.preferentialRates.map((pr, i) => (
                        <Group key={i} gap="xs">
                          <Badge variant="light" color="green">{pr.agreement}</Badge>
                          <Text size="sm" fw={600} c="green">{pr.rate}%</Text>
                          {pr.conditions && (
                            <Text size="xs" c="dimmed">({pr.conditions})</Text>
                          )}
                        </Group>
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Restrictions */}
                {data.measures.restrictions.length > 0 && (
                  <Box>
                    <Text size="sm" fw={600} c="orange" mb="xs">⚠ Restrictions</Text>
                    <List spacing="xs" size="sm">
                      {data.measures.restrictions.map((r, i) => (
                        <List.Item key={i}>
                          <Badge variant="outline" color="orange" size="xs" mr="xs">{r.type}</Badge>
                          <Text size="sm" component="span">{r.description}</Text>
                        </List.Item>
                      ))}
                    </List>
                  </Box>
                )}

                {/* Prohibitions */}
                {data.measures.prohibitions.length > 0 && (
                  <Box>
                    <Text size="sm" fw={600} c="red" mb="xs">🚫 Prohibitions</Text>
                    <List spacing="xs" size="sm">
                      {data.measures.prohibitions.map((p, i) => (
                        <List.Item key={i}>
                          <Badge variant="filled" color="red" size="xs" mr="xs">{p.type}</Badge>
                          <Text size="sm" component="span">{p.description}</Text>
                        </List.Item>
                      ))}
                    </List>
                  </Box>
                )}

                {/* Dual-Use */}
                {data.measures.dualUse && (data.measures.dualUse.category || data.measures.dualUse.regime) && (
                  <Box>
                    <Text size="sm" fw={600} c="red" mb="xs">🔒 Export Control (Dual-Use)</Text>
                    <Group gap="xs">
                      {data.measures.dualUse.category && (
                        <Badge variant="light" color="red">
                          Category: {data.measures.dualUse.category}
                        </Badge>
                      )}
                      {data.measures.dualUse.regime && (
                        <Badge variant="outline" color="red">
                          Regime: {data.measures.dualUse.regime}
                        </Badge>
                      )}
                    </Group>
                  </Box>
                )}

                {/* V2.2: Geographical Area */}
                {data.measures.geoArea && (
                  <Box>
                    <Text size="sm" fw={600} mb="xs">🌍 Geographical Area</Text>
                    <Group gap="xs">
                      <Badge variant="light" color="blue">{data.measures.geoArea.areaCode}</Badge>
                      <Text size="sm">{data.measures.geoArea.name}</Text>
                      <Badge variant="outline" color="gray" size="xs">{data.measures.geoArea.areaType}</Badge>
                    </Group>
                  </Box>
                )}

                {/* V2.2: Measure Conditions */}
                {data.measures.conditions && data.measures.conditions.length > 0 && (
                  <Box>
                    <Text size="sm" fw={600} mb="xs">📋 Measure Conditions</Text>
                    <List spacing="xs" size="sm">
                      {data.measures.conditions.map((c, i) => (
                        <List.Item key={i}>
                          <Group gap="xs">
                            <Badge variant="light" color="violet" size="xs">{c.conditionType}</Badge>
                            <Text size="sm" component="span">
                              {c.operator} {c.value !== null ? String(c.value) : "—"}
                            </Text>
                          </Group>
                        </List.Item>
                      ))}
                    </List>
                  </Box>
                )}

                {/* V2.2: Required Documents */}
                {data.measures.requiredDocuments && data.measures.requiredDocuments.length > 0 && (
                  <Box>
                    <Text size="sm" fw={600} mb="xs">📄 Required Documents</Text>
                    <List spacing="xs" size="sm">
                      {data.measures.requiredDocuments.map((d, i) => (
                        <List.Item key={i}>
                          <Group gap="xs">
                            <Badge 
                              variant={d.mandatory ? "filled" : "light"} 
                              color={d.mandatory ? "red" : "gray"} 
                              size="xs"
                            >
                              {d.documentCode}
                            </Badge>
                            <Text size="sm" component="span">
                              {d.documentName || d.description || "Document required"}
                            </Text>
                            {d.mandatory && (
                              <Badge variant="outline" color="red" size="xs">Mandatory</Badge>
                            )}
                          </Group>
                        </List.Item>
                      ))}
                    </List>
                  </Box>
                )}

                {/* No barriers message */}
                {!data.measures.hasAnyTradeBarriers && 
                  data.measures.restrictions.length === 0 && 
                  data.measures.prohibitions.length === 0 && (
                  <Text size="sm" c="green">✓ No trade barriers or restrictions apply</Text>
                )}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}
        
        {/* ─────────────── V2.3: CN MEASURES (Possible measure impact) ─────────────── */}
        {data.cnMeasures && (
          <Accordion.Item value="cn-measures">
            <Accordion.Control icon={<StepIcon icon="⚖️" color="violet" />}>
              <Group>
                <Text fw={600}>Possible measure impact</Text>
                <Badge 
                  variant="light" 
                  color={
                    data.cnMeasures.completeness.status === "COMPLETE" ? "green" :
                    data.cnMeasures.completeness.status === "PARTIAL" ? "yellow" : "red"
                  }
                  size="sm"
                >
                  {data.cnMeasures.completeness.status}
                </Badge>
                {data.cnMeasures.conditional.length > 0 && (
                  <Badge variant="outline" color="orange" size="sm">
                    {data.cnMeasures.conditional.length} conditional
                  </Badge>
                )}
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                {/* Completeness Warning */}
                {data.cnMeasures.completeness.status !== "COMPLETE" && (
                  <Alert 
                    color={data.cnMeasures.completeness.status === "PARTIAL" ? "yellow" : "red"} 
                    variant="light"
                    title="Measures data incomplete"
                  >
                    {data.cnMeasures.completeness.status === "UNAVAILABLE" 
                      ? "No CN measures data available for this HS code."
                      : `Missing data: ${data.cnMeasures.completeness.missingData.join(", ")}`}
                  </Alert>
                )}

                {/* Confirmed Measures */}
                {data.cnMeasures.confirmed.length > 0 && (
                  <Box>
                    <Text size="sm" fw={600} c="green" mb="xs">✅ Confirmed Measures ({data.cnMeasures.confirmed.length})</Text>
                    <List spacing="xs" size="sm">
                      {data.cnMeasures.confirmed.map((m, i) => (
                        <List.Item key={i}>
                          <Group gap="xs">
                            <Badge variant="light" color={m.measureType === "import" ? "blue" : "teal"} size="xs">
                              {m.measureType.toUpperCase()}
                            </Badge>
                            <Badge variant="outline" color="gray" size="xs">{m.measureTypeCode}</Badge>
                            <Text size="sm" component="span">{m.duty}</Text>
                            {m.originName && (
                              <Badge variant="light" color="violet" size="xs">{m.originName}</Badge>
                            )}
                          </Group>
                        </List.Item>
                      ))}
                    </List>
                  </Box>
                )}

                {/* Conditional Measures */}
                {data.cnMeasures.conditional.length > 0 && (
                  <Box>
                    <Text size="sm" fw={600} c="orange" mb="xs">⚠️ Conditional Measures ({data.cnMeasures.conditional.length})</Text>
                    <List spacing="xs" size="sm">
                      {data.cnMeasures.conditional.map((m, i) => (
                        <List.Item key={i}>
                          <Stack gap="xs">
                            <Group gap="xs">
                              <Badge variant="light" color={m.measureType === "import" ? "blue" : "teal"} size="xs">
                                {m.measureType.toUpperCase()}
                              </Badge>
                              <Badge variant="outline" color="gray" size="xs">{m.measureTypeCode}</Badge>
                              <Text size="sm" component="span">{m.originName || m.originCode}</Text>
                              <Badge 
                                variant="filled" 
                                color={m.requires === "CERTIFICATE" ? "orange" : m.requires === "LICENSE" ? "red" : "gray"}
                                size="xs"
                              >
                                Requires: {m.requires}
                              </Badge>
                            </Group>
                            {m.certificates.length > 0 && (
                              <Group gap="xs" ml="md">
                                {m.certificates.map((c, j) => (
                                  <Badge key={j} variant="outline" color="orange" size="xs">{c.code}</Badge>
                                ))}
                              </Group>
                            )}
                          </Stack>
                        </List.Item>
                      ))}
                    </List>
                  </Box>
                )}

                {/* Unresolved Measures */}
                {data.cnMeasures.unresolved.length > 0 && (
                  <Box>
                    <Text size="sm" fw={600} c="red" mb="xs">❌ Unresolved ({data.cnMeasures.unresolved.length})</Text>
                    <List spacing="xs" size="sm">
                      {data.cnMeasures.unresolved.map((m, i) => (
                        <List.Item key={i}>
                          <Group gap="xs">
                            <Badge variant="light" color="red" size="xs">{m.measureType}</Badge>
                            <Badge variant="outline" color="red" size="xs">{m.measureTypeCode}</Badge>
                            <Text size="sm" c="dimmed">
                              {m.reason === "MISSING_GEO_CONTEXT" && "Missing geo area context"}
                              {m.reason === "MISSING_DOCUMENT_CHAIN" && "Missing document chain"}
                              {m.reason === "EXPIRED_MEASURE" && "Measure has expired"}
                            </Text>
                          </Group>
                        </List.Item>
                      ))}
                    </List>
                  </Box>
                )}

                {/* No measures message */}
                {data.cnMeasures.confirmed.length === 0 && 
                  data.cnMeasures.conditional.length === 0 && 
                  data.cnMeasures.unresolved.length === 0 &&
                  data.cnMeasures.completeness.status === "COMPLETE" && (
                  <Text size="sm" c="green">✓ No special measures apply (standard MFN duties)</Text>
                )}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}

        {/* ─────────────── LEGAL CONSEQUENCES SUMMARY (V2.4) ─────────────── */}
        {data.legalConsequencesSummary && data.legalConsequencesSummary.length > 0 && (
          <Accordion.Item value="legal-consequences">
            <Accordion.Control icon={<StepIcon icon="⚖️" color="violet" />}>
              <Group>
                <Text fw={600}>Measures summary</Text>
                {data.legalConsequencesSummary.some(s => s.severity === "BLOCKING") && (
                  <Badge variant="filled" color="red" size="sm">Action Required</Badge>
                )}
                {!data.legalConsequencesSummary.some(s => s.severity === "BLOCKING") && 
                  data.legalConsequencesSummary.some(s => s.severity === "WARNING") && (
                  <Badge variant="filled" color="orange" size="sm">Warnings</Badge>
                )}
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                {/* Blocking items first */}
                {data.legalConsequencesSummary.filter(s => s.severity === "BLOCKING").map((item, i) => (
                  <Alert key={`blocking-${i}`} color="red" variant="light" icon={<Text>🚫</Text>}>
                    <Text size="sm" fw={600}>{item.message}</Text>
                    {item.actionRequired && (
                      <Text size="xs" c="dimmed" mt="xs">→ {item.actionRequired}</Text>
                    )}
                    {item.certificateCodes.length > 0 && (
                      <Group gap="xs" mt="xs">
                        {item.certificateCodes.map((c, j) => (
                          <Badge key={j} variant="outline" color="red" size="xs">{c}</Badge>
                        ))}
                      </Group>
                    )}
                  </Alert>
                ))}

                {/* Warnings next */}
                {data.legalConsequencesSummary.filter(s => s.severity === "WARNING").map((item, i) => (
                  <Alert key={`warning-${i}`} color="orange" variant="light" icon={<Text>⚠️</Text>}>
                    <Text size="sm" fw={600}>{item.message}</Text>
                    {item.actionRequired && (
                      <Text size="xs" c="dimmed" mt="xs">→ {item.actionRequired}</Text>
                    )}
                    {item.certificateCodes.length > 0 && (
                      <Group gap="xs" mt="xs">
                        {item.certificateCodes.map((c, j) => (
                          <Badge key={j} variant="outline" color="orange" size="xs">{c}</Badge>
                        ))}
                      </Group>
                    )}
                  </Alert>
                ))}

                {/* Info items last */}
                {data.legalConsequencesSummary.filter(s => s.severity === "INFO").map((item, i) => (
                  <Alert key={`info-${i}`} color="green" variant="light" icon={<Text>✓</Text>}>
                    <Text size="sm">{item.message}</Text>
                    {item.applicableOrigins.length > 0 && (
                      <Group gap="xs" mt="xs">
                        {item.applicableOrigins.map((o, j) => (
                          <Badge key={j} variant="light" color="violet" size="xs">{o}</Badge>
                        ))}
                      </Group>
                    )}
                  </Alert>
                ))}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}
        
        {/* ─────────────── SOURCE PROVENANCE (REMAND FIX) ─────────────── */}
        {data.sourceProvenance && (data.sourceProvenance.decisiveSource || data.sourceProvenance.evidenceChain) && (
          <Accordion.Item value="source-provenance">
            <Accordion.Control icon={<StepIcon icon="🔍" color="indigo" />}>
              <Group>
                <Text fw={600}>Source Provenance</Text>
                {data.sourceProvenance.decisiveSource && (
                  <Badge variant="light" color="indigo" size="sm">
                    {data.sourceProvenance.decisiveSource.type === "legal" ? "Legal Source" : "Nomenclature"}
                  </Badge>
                )}
              </Group>
            </Accordion.Control>
            <Accordion.Panel>
              <Stack gap="md">
                {/* Decisive Source */}
                {data.sourceProvenance.decisiveSource && (
                  <Box>
                    <Text size="sm" fw={600} mb="xs">🎯 Decisive Source</Text>
                    <Paper p="sm" withBorder radius="md" style={{ backgroundColor: 'var(--mantine-color-indigo-light)' }}>
                      <Group gap="xs" mb="xs">
                        <Badge 
                          variant="filled" 
                          color={data.sourceProvenance.decisiveSource.type === "legal" ? "violet" : "blue"}
                        >
                          {data.sourceProvenance.decisiveSource.type === "legal" ? "Legal" : "Nomenclature"}
                        </Badge>
                        {data.sourceProvenance.decisiveSource.sourceType && (
                          <Badge variant="light" color="gray">
                            {data.sourceProvenance.decisiveSource.sourceType}
                          </Badge>
                        )}
                        {data.sourceProvenance.decisiveSource.authorityLevel && (
                          <Badge 
                            variant="outline" 
                            color={
                              data.sourceProvenance.decisiveSource.authorityLevel === "PRIMARY_LEGISLATION" ? "green" :
                              data.sourceProvenance.decisiveSource.authorityLevel === "SECONDARY_LEGISLATION" ? "teal" :
                              data.sourceProvenance.decisiveSource.authorityLevel === "OFFICIAL_GUIDANCE" ? "blue" :
                              data.sourceProvenance.decisiveSource.authorityLevel === "CASE_LAW" ? "orange" : "gray"
                            }
                            size="xs"
                          >
                            {data.sourceProvenance.decisiveSource.authorityLevel.replace(/_/g, " ")}
                          </Badge>
                        )}
                        <Badge variant="light" color="green">
                          Score: {Math.round(data.sourceProvenance.decisiveSource.score * 100)}%
                        </Badge>
                      </Group>
                      <Text size="sm">{data.sourceProvenance.decisiveSource.reason}</Text>
                      {data.sourceProvenance.decisiveSource.sourceId && (
                        <Text size="xs" c="dimmed" mt="xs">
                          ID: {data.sourceProvenance.decisiveSource.sourceId}
                        </Text>
                      )}
                    </Paper>
                  </Box>
                )}
                
                {/* Evidence Chain */}
                {data.sourceProvenance.evidenceChain && data.sourceProvenance.evidenceChain.length > 0 && (
                  <Box>
                    <Text size="sm" fw={600} mb="xs">📊 Evidence Chain</Text>
                    <List spacing="xs" size="sm">
                      {data.sourceProvenance.evidenceChain.map((e, i) => (
                        <List.Item key={i}>
                          <Group gap="xs">
                            <Badge 
                              variant={e.role === "AUTHORITATIVE" ? "filled" : e.role === "SUPPORTING" ? "light" : "outline"}
                              color={
                                e.role === "AUTHORITATIVE" ? "green" :
                                e.role === "SUPPORTING" ? "blue" : "gray"
                              }
                              size="xs"
                            >
                              {e.role}
                            </Badge>
                            <Badge variant="light" color={e.sourceType === "legal" ? "violet" : "blue"} size="xs">
                              {e.sourceType}
                            </Badge>
                            <Text size="sm" component="span" style={{ flex: 1 }}>
                              {e.identifier.length > 30 ? e.identifier.substring(0, 30) + "..." : e.identifier}
                            </Text>
                            <Badge variant="filled" color="indigo" size="sm">
                              {e.contribution}%
                            </Badge>
                            {e.authorityLevel && (
                              <Badge variant="outline" color="gray" size="xs">
                                {e.authorityLevel.replace(/_/g, " ")}
                              </Badge>
                            )}
                          </Group>
                        </List.Item>
                      ))}
                    </List>
                  </Box>
                )}
              </Stack>
            </Accordion.Panel>
          </Accordion.Item>
        )}
      </Accordion>
    </Paper>
  );
}

export default ClassificationExplainabilityTree;
